import React, {
  useContext, lazy,
} from 'react';
import { useSelector } from 'react-redux';
import { Switch, useLocation } from 'react-router-dom';
import GuestRoutes from 'routes/Guest';
import LoggedRoutes from 'routes/Logged';
import LoggedRoutesV2 from 'routes/LoggedV2';

import { checkIsRead } from 'helpers/checkIsRead';
import { FeatureFlags } from 'context/featureFlag';
import TawkTo from 'components/layouts/tawkTo';
import { useTawkVisibility } from 'components/layouts/useTawkVisibility';
import Metabase from 'pages/metabase';
import testPage from 'components/_parts/atoms/testPage';

interface AuthRoleState {
  readonly user_type?: string | number;
  readonly user_type_ket?: string;
}

interface RoutesRootState {
  readonly auth?: {
    readonly role?: AuthRoleState;
  };
}

interface FeatureFlagValue {
  readonly businessProcess?: boolean;
}

const lazyPage = (path: string) => lazy(() => import(path));

// layouts
const Page404 = lazyPage('components/layouts/404');
const Status403 = lazyPage('components/layouts/403');

// auth
const ForgotPass = React.lazy(() => import('components/auth/forgotPassword'));
const Login = React.lazy(() => import('pages/login'));
const SignUp = React.lazy(() => import('components/auth/register'));

// board
const Board = React.lazy(() => import('pages/board'));
const DetailBoard = React.lazy(() => import('pages/board/detailBoard'));
const MemberBoard = React.lazy(() => import('pages/board/memberBoard'));

// dashboard
const Dashboard = React.lazy(() => import('pages/dashboard'));
const DashboardRevamp = React.lazy(() => import('pages/dashboardRevamp'));

// company
const Company = React.lazy(() => import('pages/company'));

// business process
const CVC_LVL = React.lazy(() => import('components/businessProcess/companyValueChain/Level_2'));
const BusinessProcessCreate = React.lazy(() => import('components/businessProcess/indexView/FormAddProcess'));
const BusinessProcessUpdate = React.lazy(() => import('components/businessProcess/indexView/FormEditProcess'));
const TreeView = React.lazy(() => import('components/businessProcess/treeView'));
const BusinessProcessCreateTree = React.lazy(() => import('components/businessProcess/treeView/FormAdd'));
const BusinessProcessCreateTreeChild = React.lazy(() => import('components/businessProcess/treeView/FormAddChild'));
const UpdateTreeView = React.lazy(() => import('components/businessProcess/treeView/FormEdit'));
const CVC = React.lazy(() => import('pages/businessProcess/cvc'));
const IndexView = React.lazy(() => import('pages/businessProcess/IndexView'));

// organizations
const Apqc = React.lazy(() => import('components/businessProcess/companyValueChain/apqc'));
const EmployeesIndex = React.lazy(() => import('components/organizations/employees'));
const EmployeesCreate = React.lazy(() => import('components/organizations/employees/AddForm'));
const EmployeesUpdate = React.lazy(() => import('components/organizations/employees/EditForm'));
const StructureCreate = React.lazy(() => import('components/organizations/index-view/AddStructure'));
const StructureUpdate = React.lazy(() => import('components/organizations/index-view/EditStructure'));
const JobWizard = React.lazy(() => import('components/organizations/index-view/jobWizard/EditStructure'));
const IndexViewOrganization = React.lazy(() => import('pages/organizations/index-view'));
const StructureView = React.lazy(() => import('pages/organizations/structure-view/index'));

// matrix
const DndMatrix = React.lazy(() => import('components/matrix/drag-n-drop'));
const IndexMatrix = React.lazy(() => import('components/matrix/indexMatrix'));
const AuditMatrix = React.lazy(() => import('components/matrix/indexMatrix/auditMatrix'));
const BlankMatrixOri = React.lazy(() => import('components/matrix/indexMatrix/blankMatrix'));

// jobdesc
const JobDescDetail = React.lazy(() => import('pages/jobDesc/detail'));
const JobDesc = React.lazy(() => import('pages/jobDesc/index'));
const JobDescPrint = React.lazy(() => import('pages/jobDesc/PrintView'));

// job level
const IndexJobLevel = React.lazy(() => import('components/organizations/job-level'));
const AddJobLevel = React.lazy(() => import('components/organizations/job-level/add'));
const EditJobLevel = React.lazy(() => import('components/organizations/job-level/edit'));

// pivot
const Pivot = React.lazy(() => import('components/matrix/pivotTable'));

// TnC
const TermsAndCondition = React.lazy(() => import('pages/termsAndCondition'));

// profile
const BackOffice = React.lazy(() => import('components/backoffice/company'));
const BackOfficeCreate = React.lazy(() => import('components/backoffice/company/AddForm'));
const BackOfficeUpdate = React.lazy(() => import('components/backoffice/company/EditForm'));
const Profile = React.lazy(() => import('components/profile'));
const BackOfficeCompanyDetail = React.lazy(() => import('components/backoffice/company/detail/detail'));
const BackOfficeCompanyDetailBoard = React.lazy(() => import('components/backoffice/company/detail/DetailBoard'));

const BackOfficeUserManagement = React.lazy(() => import('components/backoffice/userManagement'));
const BackOfficeUserManagementCreate = React.lazy(() => import('components/backoffice/userManagement/AddForm'));
const BackOfficeUserManagementUpdate = React.lazy(() => import('components/backoffice/userManagement/EditForm'));

const BackOfficeLog = React.lazy(() => import('components/backoffice/log'));
const BackOfficeLogDetail = React.lazy(() => import('components/backoffice/log/EditForm'));

const BackOfficeSubscription = React.lazy(() => import('components/backoffice/subscription'));
const BackOfficeSubscriptionCreate = React.lazy(() => import('components/backoffice/subscription/AddForm'));
const BackOfficeSubscriptionUpdate = React.lazy(() => import('components/backoffice/subscription/EditForm'));

const BackOfficeDashboard = React.lazy(() => import('components/backoffice/dashboard'));

const BackOfficeApqc = React.lazy(() => import('components/backoffice/apqc'));
const BackOfficeApqcDetail = React.lazy(() => import('components/backoffice/apqc/Details'));
const BackOfficeApqcCreate = React.lazy(() => import('components/backoffice/apqc/FormAdd'));
const BackOfficeApqcUpdate = React.lazy(() => import('components/backoffice/apqc/FormEdit'));

// workload
const WorkloadList = React.lazy(() => import('components/backoffice/workload/list'));
const WorkloadAssessment = React.lazy(() => import('components/backoffice/workload/assessment'));
const WorkloadAssessment1 = React.lazy(() => import('components/backoffice/workload/assessment/step2'));
const WorkloadAssessment2 = React.lazy(() => import('components/backoffice/workload/assessment/step3'));
const WorkloadAssessment3 = React.lazy(() => import('components/backoffice/workload/assessment/final'));
const WorkloadDetail = React.lazy(() => import('components/backoffice/workload/detail'));

const WorkloadLanding = React.lazy(() => import('pages/workloadAssessment/landing'));
const WorkloadLandingV2 = React.lazy(() => import('pages/workloadAssessment/landing/revamp'));
const WorkloadLandingStep1 = React.lazy(() => import('pages/workloadAssessment/landing/step1'));
const WorkloadLandingStep1Onsave = React.lazy(() => import('pages/workloadAssessment/landing/step1_onsave'));
const WorkloadLandingStep2SuccessFinalsubmit = React.lazy(() => import('pages/workloadAssessment/landing/submit_finalsubmit'));
const WorkloadLandingStep2SuccessOnsave = React.lazy(() => import('pages/workloadAssessment/landing/submit_onsave'));

const WorkloadExport = React.lazy(() => import('components/workloadAssessment/analytics/parts/exports'));

// databank
const coreIndex = React.lazy(() => import('components/backoffice/organizationDataBank/coreCompetencies'));
const coreAdd = React.lazy(() => import('components/backoffice/organizationDataBank/coreCompetencies/form/add'));
const coreUpdate = React.lazy(() => import('components/backoffice/organizationDataBank/coreCompetencies/form/edit'));

const functionalIndex = React.lazy(() => import('components/backoffice/organizationDataBank/functionalCompentencies'));
const functionalAdd = React.lazy(() => import('components/backoffice/organizationDataBank/functionalCompentencies/form/add'));
const functionalUpdate = React.lazy(() => import('components/backoffice/organizationDataBank/functionalCompentencies/form/edit'));

const leadIndex = React.lazy(() => import('components/backoffice/organizationDataBank/leadCompetencies'));
const leadAdd = React.lazy(() => import('components/backoffice/organizationDataBank/leadCompetencies/form/add'));
const leadUpdate = React.lazy(() => import('components/backoffice/organizationDataBank/leadCompetencies/form/edit'));

// public auth
const PublicAuth = React.lazy(() => import('components/publicAuth'));
const ConfirmationPayment = React.lazy(() => import('components/publicAuth/confirmation'));

// WLA dashboard
const WLADepartement = React.lazy(() => import('components/workloadAssessment/analytics/departement'));
const WLADivision = React.lazy(() => import('components/workloadAssessment/analytics'));
const WLAEmployee = React.lazy(() => import('components/workloadAssessment/analytics/employee'));
const WLAJobLVL = React.lazy(() => import('components/workloadAssessment/analytics/jobtitle'));
const WLAProcess = React.lazy(() => import('components/workloadAssessment/analytics/parts/processDetail'));

// WLA status
const WLAList = React.lazy(() => import('pages/workloadAssessment/statusWla'));
const WLADetail = React.lazy(() => import('pages/workloadAssessment/statusWla/detail'));

// KPI
const Activity = React.lazy(() => import('components/performaceTracker/activity'));
const ActivityCreate = React.lazy(() => import('components/performaceTracker/activity/create'));
const KPIDashboardAdmin = React.lazy(() => import('components/performaceTracker/homepage/admin'));
const KPIDashboardEmployee = React.lazy(() => import('components/performaceTracker/homepage/employee'));
const KPIList = React.lazy(() => import('components/performaceTracker/kpi'));
const ActionList = React.lazy(() => import('components/performaceTracker/kpi/action'));
const ActionDetail = React.lazy(() => import('components/performaceTracker/kpi/action/detail'));
const ActionEdit = React.lazy(() => import('components/performaceTracker/kpi/action/edit'));
const KPIDetail = React.lazy(() => import('components/performaceTracker/kpi/detail'));
const KPILogin = React.lazy(() => import('components/performaceTracker/login'));

const KPIAll = React.lazy(() => import('components/performaceTracker/kpi/all'));
const KPIKr = React.lazy(() => import('components/performaceTracker/kpi/all/createKeyResult'));
const KPIObjective = React.lazy(() => import('components/performaceTracker/kpi/all/createObjective'));
const KPICompany = React.lazy(() => import('components/performaceTracker/kpi/company'));
const KPICompanyAction = React.lazy(() => import('components/performaceTracker/kpi/company/action'));
const KPICompanyActionDetail = React.lazy(() => import('components/performaceTracker/kpi/company/action/detail'));
const KPICompanyView = React.lazy(() => import('components/performaceTracker/kpi/company/view'));
const KPIEmployee = React.lazy(() => import('components/performaceTracker/kpi/employee'));

// revamp
const ptManageDepartmentDetail = React.lazy(() => import('_revamp/organism/kpi/manage_department/detail'));
const ptManageEmployeeDetail = React.lazy(() => import('_revamp/organism/kpi/manage_employee/detail'));
const ptManageTeamDetail = React.lazy(() => import('_revamp/organism/kpi/manage_team/detail'));
const ptManageEmployee = React.lazy(() => import('_revamp/page/kpi/admin/admnistrative/manageEmployee'));
const ptManageEmployeeCreate = React.lazy(() => import('_revamp/page/kpi/admin/admnistrative/manageEmployee/create'));
const ptManageDepartment = React.lazy(() => import('_revamp/page/kpi/admin/admnistrative/manage_department'));
const ptManageTeam = React.lazy(() => import('_revamp/page/kpi/admin/admnistrative/manage_team'));
const ptDashboard = React.lazy(() => import('_revamp/page/kpi/admin/home/dashboard'));
const ptInitialSetup = React.lazy(() => import('_revamp/page/kpi/admin/home/initial_setup'));
const ptActionPlan = React.lazy(() => import('_revamp/page/kpi/admin/performance/action_plan'));
const ptAssignment = React.lazy(() => import('_revamp/page/kpi/admin/performance/assignment'));
const ptBusinessProcess = React.lazy(() => import('_revamp/page/kpi/admin/performance/assignment/business_process'));
const ptInitative = React.lazy(() => import('_revamp/page/kpi/admin/performance/initiative'));
const ptOkr = React.lazy(() => import('_revamp/page/kpi/admin/performance/okr'));
const ptKr = React.lazy(() => import('_revamp/page/kpi/admin/performance/okr/kr'));
const ptObjective = React.lazy(() => import('_revamp/page/kpi/admin/performance/okr/objective'));
const ptDepartPerformance = React.lazy(() => import('_revamp/page/kpi/admin/reporting/department_performance'));
const ptEmployeePerformance = React.lazy(() => import('_revamp/page/kpi/admin/reporting/employee_performance'));
const ptOkrPerformance = React.lazy(() => import('_revamp/page/kpi/admin/reporting/okr_performance'));
const ptOverviewPerformance = React.lazy(() => import('_revamp/page/kpi/admin/reporting/overview_performance'));

const ptLogin = React.lazy(() => import('_revamp/page/kpi/admin/login_admin'));
const ptDepartmentEmployee = React.lazy(() => import('_revamp/page/kpi/admin/reporting/department_employee'));
const ptDashboardEmployee = React.lazy(() => import('_revamp/page/kpi/employee/home/dashboardEmployee'));
const ptLoginEmployee = React.lazy(() => import('_revamp/page/kpi/employee/login_employee'));
const ptActionPlanEmployee = React.lazy(() => import('_revamp/page/kpi/employee/performance/action_plan'));
const ptInitiativeEmployee = React.lazy(() => import('_revamp/page/kpi/employee/performance/initiative'));
const ptOkrEmployee = React.lazy(() => import('_revamp/page/kpi/employee/performance/okr'));
const ptReportEmployee = React.lazy(() => import('_revamp/page/kpi/employee/reporting/report_employee'));
const ptTeamEmployee = React.lazy(() => import('_revamp/page/kpi/employee/reporting/team_employee'));
function Routes() {
  const { userType, userTypeKet } = useSelector((state: RoutesRootState) => ({
    userType: state.auth?.role?.user_type,
    userTypeKet: state.auth?.role?.user_type_ket,
  }));

  const featureFlagListed = useContext(FeatureFlags) as FeatureFlagValue;
  const location = useLocation();
  useTawkVisibility();
  return (
    <>
      <TawkTo />
      <Switch key={location.pathname} location={location}>
        {/* auth */}
        <GuestRoutes
          exact
          path="/administrator"
          component={Login}
          header={false}
          admin
        />
        <GuestRoutes exact path="/signin" component={Login} header={false} />
        <GuestRoutes
          exact
          path="/workload-landing"
          component={WorkloadLanding}
          header={false}
          bypass
        />
        <GuestRoutes
          exact
          path="/metabase"
          component={Metabase}
          header={false}
          bypass
        />
        <GuestRoutes
          exact
          path="/workload-landing-v2"
          component={WorkloadLanding}
          header={false}
          bypass
        />
        <GuestRoutes
          exact
          path="/workload-landing-v2/onsave/1"
          component={WorkloadLandingV2}
          header={false}
          bypass
        />
        <GuestRoutes
          exact
          path="/only-admin-wla"
          component={WorkloadLanding}
          header={false}
          bypass
        />
        <GuestRoutes
          exact
          path="/workload-landing/1"
          component={WorkloadLandingStep1}
          header={false}
          bypass
          protected="wla"
        />
        <GuestRoutes
          exact
          path="/workload-landing-v2/1"
          component={WorkloadLandingV2}
          header={false}
          bypass
          protected="wla"
        />
        <GuestRoutes
          exact
          path="/workload-landing/onsave/1"
          component={WorkloadLandingStep1Onsave}
          header={false}
          bypass
          protected="wla"
        />
        <GuestRoutes
          exact
          path="/workload-landing/success_onsave"
          component={WorkloadLandingStep2SuccessOnsave}
          header={false}
          bypass
          protected="wla"
        />
        <GuestRoutes
          exact
          path="/workload-landing/success_finalsubmit"
          component={WorkloadLandingStep2SuccessFinalsubmit}
          header={false}
          bypass
          protected="wla"
        />
        <GuestRoutes exact path="/" component={Login} header={false} />
        <GuestRoutes exact path="/signup" component={SignUp} header={false} />
        <GuestRoutes
          exact
          path="/public_auth"
          component={PublicAuth}
          header={false}
        />
        <GuestRoutes
          exact
          path="/confirmation_payment"
          component={ConfirmationPayment}
          header={false}
        />
        <GuestRoutes
          exact
          path="/forgot-pass"
          component={ForgotPass}
          header={false}
        />
        <GuestRoutes
          exact
          path="/kpi-login"
          component={KPILogin}
          header={false}
        />

        {/* board */}
        <LoggedRoutes
          exact
          path="/kpi"
          component={KPIDashboardEmployee}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/kpi-list"
          component={KPIList}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/kpi-list/:id"
          component={KPIDetail}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/action/:id"
          component={ActionList}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/action-detail/:id"
          component={ActionDetail}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/action-edit/:id"
          component={ActionEdit}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/activity"
          component={Activity}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/activity/create"
          component={ActivityCreate}
          header
          loginType="kpi"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin"
          component={KPIDashboardAdmin}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/company"
          component={KPICompany}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/company/:id"
          component={KPICompanyView}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/company/action/:id"
          component={KPICompanyAction}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/company/action-detail/:id"
          component={KPICompanyActionDetail}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/employee"
          component={KPIEmployee}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/all"
          component={KPIAll}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/all/keyresult/create"
          component={KPIKr}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutes
          exact
          path="/kpi-admin/all/objective/create"
          component={KPIObjective}
          header
          loginType="kpi-admin"
        />
        {/* revamp - performance tracker */}
        <GuestRoutes
          exact
          path="/v2/kpi/login-employee"
          component={ptLoginEmployee}
          header={false}
          loginType="pt-employee"
        />
        <GuestRoutes
          exact
          path="/v2/kpi/login"
          component={ptLogin}
          header={false}
          loginType="pt-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/dashboard"
          component={ptDashboard}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/initial-setup"
          component={ptInitialSetup}
          header={false}
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/okr"
          component={ptOkr}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/initiative"
          component={ptInitative}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/action-plan"
          component={ptActionPlan}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/objective"
          component={ptObjective}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/kr"
          component={ptKr}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/assignment"
          component={ptAssignment}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/create-bp"
          component={ptBusinessProcess}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/manage-team"
          component={ptManageTeam}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/manage-department"
          component={ptManageDepartment}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/manage-team/:team"
          component={ptManageTeamDetail}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/manage-department/:department"
          component={ptManageDepartmentDetail}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/manage-employee"
          component={ptManageEmployee}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/manage-employee/:employee"
          component={ptManageEmployeeDetail}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/manage-employee-create"
          component={ptManageEmployeeCreate}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/overview-performance"
          component={ptOverviewPerformance}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/okr-performance"
          component={ptOkrPerformance}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/department-performance"
          component={ptDepartPerformance}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/employee-performance"
          component={ptEmployeePerformance}
          header
          loginType="kpi-admin"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/dashboard-employee"
          component={ptDashboardEmployee}
          header
          loginType="pt-employee"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/report"
          component={ptReportEmployee}
          header
          loginType="pt-employee"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/team-employee"
          component={ptTeamEmployee}
          header
          loginType="pt-employee"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/department-employee"
          component={ptDepartmentEmployee}
          header
          loginType="pt-employee"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/okr-employee"
          component={ptOkrEmployee}
          header
          loginType="pt-employee"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/initiative-employee"
          component={ptInitiativeEmployee}
          header
          loginType="pt-employee"
        />
        <LoggedRoutesV2
          exact
          path="/v2/kpi/action-plan-employee"
          component={ptActionPlanEmployee}
          header
          loginType="pt-employee"
        />
        {/* revamp - performance tracker */}
        <LoggedRoutes exact path="/board" component={Board} header />
        <LoggedRoutes
          exact
          path="/detail-board/:id"
          component={DetailBoard}
          header
        />
        <LoggedRoutes
          exact
          path="/detail-board/member/:id"
          component={MemberBoard}
          header
        />
        {/* dashboard */}
        <LoggedRoutes exact path="/dashboard" component={DashboardRevamp} header />
        <LoggedRoutes exact path="/dashboard-revamp" component={DashboardRevamp} header />
        {/* company */}
        <LoggedRoutes exact path="/company" component={Company} header />
        {/* BusinessProcess */}
        <LoggedRoutes
          exact
          path="/business-process/indexview"
          component={IndexView}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process"
          component={TreeView}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process/tree-view/update/:id"
          component={UpdateTreeView}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process/cvc"
          component={CVC}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process/cvc/lvl-:num/:id"
          component={CVC_LVL}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process/update/:id"
          component={BusinessProcessUpdate}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process/create/:id/:type"
          component={BusinessProcessCreate}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process/create-tree/:id/:act"
          component={BusinessProcessCreateTree}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        <LoggedRoutes
          exact
          path="/business-process/create-tree-child/:id/:lvl/:parent"
          component={BusinessProcessCreateTreeChild}
          header
          isFeatureListed={featureFlagListed.businessProcess}
        />
        {/* Organizations */}
        {/* <LoggedRoutes
        exact
        path="/organizations/update"
        component={Organization_Update}
        header={true}
      /> */}
        {/* <LoggedRoutes
        exact
        path="/organizations/create"
        component={Organization_Create}
        header={true}
      /> */}
        <LoggedRoutes
          exact
          path="/organizations/create"
          component={StructureCreate}
          header
        />
        <LoggedRoutes
          exact
          path="/organizations/update/:id"
          component={StructureUpdate}
          header
        />
        <LoggedRoutes
          exact
          path="/organizations/jobwizard"
          component={JobWizard}
          header
        />
        <LoggedRoutes
          exact
          path="/employees"
          component={EmployeesIndex}
          header
        />
        <LoggedRoutes
          exact
          path="/employees/create"
          component={
            checkIsRead(userType) === true && userTypeKet === 'read'
              ? Dashboard
              : EmployeesCreate
          }
          header
        />
        <LoggedRoutes
          exact
          path="/employees/update/:id"
          component={
            checkIsRead(userType) === true && userTypeKet === 'read'
              ? Dashboard
              : EmployeesUpdate
          }
          header
        />
        <LoggedRoutes
          exact
          path="/structure-view"
          component={StructureView}
          header
        />
        <LoggedRoutes exact path="/organizations" component={IndexViewOrganization} header />
        {/* Matrix */}
        <LoggedRoutes
          exact
          path="/matrix"
          component={IndexMatrix}
          header
          matrix
        />
        <LoggedRoutes exact path="/apqc" component={Apqc} header />
        <LoggedRoutes
          exact
          path="/blank-matrix/:id"
          component={AuditMatrix}
          header
          matrix
        />
        <LoggedRoutes
          exact
          path="/audit-matrix/:id"
          component={AuditMatrix}
          header
          matrix
        />
        <LoggedRoutes
          exact
          path="/blank-matrix-dummy"
          component={BlankMatrixOri}
          header
          matrix
        />
        <LoggedRoutes
          exact
          path="/dnd-matrix"
          component={DndMatrix}
          header
          matrix
        />
        <LoggedRoutes exact path="/job-desc" component={JobDesc} header />
        <LoggedRoutes
          exact
          path="/job-desc/detail"
          component={JobDescDetail}
          header
        />
        <LoggedRoutes
          path="/job-desc/print"
          component={JobDescPrint}
          header={false}
        />
        <LoggedRoutes exact path="/403" component={Status403} header />
        {/* job desc lvl */}
        <LoggedRoutes exact path="/job-level" component={IndexJobLevel} header />
        <LoggedRoutes
          exact
          path="/job-level/edit"
          component={EditJobLevel}
          header
        />
        <LoggedRoutes
          exact
          path="/job-level/add"
          component={AddJobLevel}
          header
        />
        <LoggedRoutes exact path="/pivot-table" component={Pivot} header />
        <LoggedRoutes exact path="/profile" component={Profile} header />
        <LoggedRoutes
          exact
          path="/back-office/dashboard"
          component={BackOfficeDashboard}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/company"
          component={BackOffice}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/company-detail"
          component={BackOfficeCompanyDetail}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/company-detail-board"
          component={BackOfficeCompanyDetailBoard}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          path="/back-office/job-desc/print"
          component={JobDescPrint}
          header={false}
        />
        <LoggedRoutes
          exact
          path="/back-office/create"
          component={BackOfficeCreate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/update"
          component={BackOfficeUpdate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/user-management"
          component={BackOfficeUserManagement}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/user-management/create"
          component={BackOfficeUserManagementCreate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/user-management/update"
          component={BackOfficeUserManagementUpdate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/log"
          component={BackOfficeLog}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/log/detail"
          component={BackOfficeLogDetail}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/subscription"
          component={BackOfficeSubscription}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/subscription/create"
          component={BackOfficeSubscriptionCreate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/subscription/update"
          component={BackOfficeSubscriptionUpdate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/apqc"
          component={BackOfficeApqc}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/apqc/update"
          component={BackOfficeApqcUpdate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/apqc/details"
          component={BackOfficeApqcDetail}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/back-office/apqc/create"
          component={BackOfficeApqcCreate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/workload/list"
          component={WorkloadList}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/workload/create"
          component={WorkloadAssessment}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/workload/create/2"
          component={WorkloadAssessment1}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/workload/create/3"
          component={WorkloadAssessment2}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/workload/create/4"
          component={WorkloadAssessment3}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/workload/detail"
          component={WorkloadDetail}
          header
          bo
          loginType="admin"
        />
        {/* core competencies */}
        <LoggedRoutes
          exact
          path="/databank/competency"
          component={coreIndex}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/databank/competency/add"
          component={coreAdd}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/databank/competency/update/:id"
          component={coreUpdate}
          header
          bo
          loginType="admin"
        />
        {/* functional competencies */}
        <LoggedRoutes
          exact
          path="/databank/functional-competencies"
          component={functionalIndex}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/databank/functional-competencies/add"
          component={functionalAdd}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/databank/functional-competencies/update"
          component={functionalUpdate}
          header
          bo
          loginType="admin"
        />
        {/* lead competencies */}
        <LoggedRoutes
          exact
          path="/databank/lead-competencies"
          component={leadIndex}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/databank/lead-competencies/add"
          component={leadAdd}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          exact
          path="/databank/lead-competencies/update"
          component={leadUpdate}
          header
          bo
          loginType="admin"
        />
        <LoggedRoutes
          path="/wla/dashboard-employee"
          component={WLAEmployee}
          header
          // bo={true}
        />
        <LoggedRoutes
          path="/wla/dashboard-process"
          component={WLAProcess}
          header
          // bo={true}
        />
        <LoggedRoutes
          path="/wla/dashboard-division"
          component={WLADivision}
          header
          // bo={true}
        />
        <LoggedRoutes
          path="/wla/dashboard-departement"
          component={WLADepartement}
          header
          // bo={true}
        />
        <LoggedRoutes
          path="/wla/dashboard-job-title"
          component={WLAJobLVL}
          header
          // bo={true}
        />
        <LoggedRoutes
          exact
          path="/wla/list"
          component={WLAList}
          header
          // bo={true}
        />
        <LoggedRoutes
          exact
          path="/wla/detail"
          component={WLADetail}
          header
          // bo={true}
        />
        {/* export wla */}
        <LoggedRoutes
          exact
          path="/wla/export/:type/:job_title"
          component={WorkloadExport}
          header={false}
          // bo={true}
        />

        <LoggedRoutes
          exact
          path="/test-page"
          component={testPage}
          header={false}
        />
        {/* export wla */}
        <LoggedRoutes
          exact
          path="/terms-and-condition"
          header={false}
          component={TermsAndCondition}
        />
        <GuestRoutes exact path="*" component={Page404} />
      </Switch>
    </>
  );
}

export default Routes;
